import { createContext, useCallback, useContext, useState, ReactNode } from 'react';
import { createPortal } from 'react-dom';

type Kind = 'success' | 'error' | 'info';
// `duration` lets callers override the default 3.2 s lifetime — quiet
// background pings like "voice chat enabled" feel less intrusive at
// ~1.8 s, while errors benefit from the full 3.2 s so the user has time
// to read them.
type Item = { id: number; kind: Kind; text: string };
type Ctx = { push: (kind: Kind, text: string, durationMs?: number) => void };

const C = createContext<Ctx | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Item[]>([]);
  const push = useCallback((kind: Kind, text: string, durationMs: number = 3200) => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs, { id, kind, text }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), durationMs);
  }, []);
  // Render the toast stack via a portal directly onto document.body.
  //
  // Why: `position: fixed` is relative to the viewport ONLY if no
  // ancestor has a transform / filter / backdrop-filter / will-change /
  // perspective. If ANY ancestor creates a containing block (and the
  // chess / rummy pages do — they use `backdrop-filter` on full-viewport
  // frames and CSS transforms on board wrappers), the toast div gets
  // clipped / stacked inside THAT ancestor's stacking context and z-
  // index fights become meaningless. Portaling to `document.body` is
  // the one place the browser GUARANTEES no parent stacking context
  // exists — so `fixed top-3 z-[100000]` is actually at the top of the
  // viewport above everything, on every page, on every device.
  //
  // SSR guard: during the initial server render there is no
  // `document` — we skip the portal then. Our app is client-side only
  // (Vite SPA) so in practice this branch never fires, but keeps the
  // type narrow and avoids blowing up if someone later adds SSR.
  const portalHost = typeof document !== 'undefined' ? document.body : null;
  const toastStack = (
    <div className="fixed top-3 left-0 right-0 z-[2147483647] flex flex-col items-center gap-2 px-4 pointer-events-none">
      {items.map((t) => (
        <ToastItem key={t.id} kind={t.kind} text={t.text} />
      ))}
    </div>
  );
  return (
    <C.Provider value={{ push }}>
      {children}
      {portalHost ? createPortal(toastStack, portalHost) : toastStack}
    </C.Provider>
  );
}

// Glass-pill toast: backdrop-blurred translucent background, subtle
// coloured left border + icon tinted to kind. Tailwind doesn't ship
// `backdrop-blur` in older configs but every modern browser respects
// the inline fallback; we layer a semi-opaque bg so it degrades cleanly.
function ToastItem({ kind, text }: { kind: Kind; text: string }) {
  const accent =
    kind === 'success' ? 'before:bg-emerald-400' :
    kind === 'error'   ? 'before:bg-rose-500'    :
                         'before:bg-sky-400';
  const icon =
    kind === 'success' ? '✓' :
    kind === 'error'   ? '!' :
                         'ⓘ';
  const iconColor =
    kind === 'success' ? 'text-emerald-300' :
    kind === 'error'   ? 'text-rose-300'    :
                         'text-sky-300';
  return (
    <div
      className={
        // Pill layout: pseudo-element serves as the coloured accent strip
        // on the left; the body is a frosted dark panel so the toast sits
        // well over both the bright lobby and the dark game board.
        'pointer-events-auto relative overflow-hidden ' +
        'flex items-center gap-2.5 pl-4 pr-5 py-2 ' +
        'rounded-full shadow-xl ring-1 ring-white/10 ' +
        'text-[13px] font-medium text-white/95 ' +
        'animate-slide-down ' +
        // backdrop blur + translucent bg (solid fallback first so old
        // browsers without backdrop-filter still look intentional).
        'bg-neutral-900/75 [@supports(backdrop-filter:blur(8px))]:bg-neutral-900/55 ' +
        '[@supports(backdrop-filter:blur(8px))]:backdrop-blur-md ' +
        // Left-edge colour strip via ::before. `accent` selects bg.
        'before:content-[""] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[3px] ' +
        accent
      }
      role="status"
    >
      <span
        className={
          'inline-flex h-5 w-5 items-center justify-center rounded-full ' +
          'bg-white/10 text-[11px] font-bold ' + iconColor
        }
      >
        {icon}
      </span>
      <span className="leading-snug">{text}</span>
    </div>
  );
}

export function useToast() {
  const v = useContext(C);
  if (!v) throw new Error('useToast outside provider');
  return v;
}
